// Gem Run: a platformer. Run, jump, collect gems, reach the flag. Three generated levels per
// visit. Falling only puts you back at the last checkpoint. A fun game: no gems paid out.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import { int, chance, type Rng } from '@kit/rng';
import { mapFromStrings, moveBody, drawMap, followCamera, makePad, setTile, type Body, type Camera, type TileMap } from '@kit/tilemap';
import { drawSprite, type Facing } from '@avatar/render';
import type { GameModule, GameContext } from '@shell/types';

const T = 24, ROWS = 10;
const SKY = 0, GROUND = 1, BRICK = 2, GEM = 3, FLAG = 4, SPRING = 5, CHECK = 6, SLIME = 7;
const GRAVITY = 1000, JUMP = -360, RUN = 130, SPRING_V = -560;

function genLevel(rng: Rng, n: number): { map: TileMap; gems: number; slimes: { x: number; y: number; dir: number; min: number; max: number }[] } {
  const rows: string[] = Array.from({ length: ROWS }, () => '');
  let x = 0;
  let gems = 0;
  const slimes: { x: number; y: number; dir: number; min: number; max: number }[] = [];
  const put = (col: string[]) => { for (let r = 0; r < ROWS; r++) rows[r] += col[r]; x++; };
  const ground = (h: number, gem = false, extra = '') => {
    const col = Array.from({ length: ROWS }, () => '.');
    for (let r = ROWS - h; r < ROWS; r++) col[r] = r === ROWS - h ? '#' : '=';
    if (gem) col[ROWS - h - 2] = 'o';
    if (extra && ROWS - h - 1 >= 0) col[ROWS - h - 1] = extra;
    put(col);
  };
  const gap = (w: number) => { for (let i = 0; i < w; i++) put(Array.from({ length: ROWS }, () => '.')); };
  // start platform
  for (let i = 0; i < 6; i++) ground(2, i === 4);
  let h = 2;
  const segments = 9 + n * 2;
  for (let s = 0; s < segments; s++) {
    const kind = rng();
    if (kind < 0.3) { gap(int(rng, 2, 3 + Math.min(n, 1))); }
    else if (kind < 0.45 && h < 6) { h += int(rng, 1, 2); }
    else if (kind < 0.6 && h > 2) { h -= int(rng, 1, 2); }
    const len = int(rng, 3, 7);
    const spring = chance(rng, 0.15) && h <= 4;
    for (let i = 0; i < len; i++) {
      const extra = spring && i === len - 2 ? 'S' : (i === 1 && chance(rng, 0.35) ? 'C' : '');
      ground(h, i % 2 === 1 && chance(rng, 0.8), extra);
    }
    if (len >= 5 && n >= 1 && chance(rng, 0.5)) {
      slimes.push({ x: (x - len + 1) * T, y: (ROWS - h - 1) * T, dir: 1, min: (x - len + 1) * T, max: (x - 2) * T });
    }
    // floating brick with a gem above a long run
    if (len >= 5 && chance(rng, 0.6)) {
      const bx = x - Math.floor(len / 2), by = ROWS - h - 4;
      if (by > 1) { rows[by] = rows[by].slice(0, bx) + 'B' + rows[by].slice(bx + 1); rows[by - 1] = rows[by - 1].slice(0, bx) + 'o' + rows[by - 1].slice(bx + 1); }
    }
  }
  for (let i = 0; i < 6; i++) ground(2, false, i === 3 ? 'F' : '');
  const map = mapFromStrings(rows, { '.': SKY, '#': GROUND, '=': BRICK, 'o': GEM, 'F': FLAG, 'S': SPRING, 'C': CHECK, 'B': BRICK }, T, [GROUND, BRICK]);
  gems = 0; for (let i = 0; i < map.data.length; i++) if (map.data[i] === GEM) gems++;
  return { map, gems, slimes };
}

function mount(host: HTMLElement, ctx: GameContext) {
  let levelIdx = 0;
  let total = 0, totalGems = 0, got = 0, falls = 0;
  let { map, gems, slimes } = genLevel(ctx.rng, 0);
  totalGems += gems;
  const body: Body = { x: T * 1.5, y: (ROWS - 2) * T - 26, w: 13, h: 24, vx: 0, vy: 0 };
  let facing: Facing = 'right';
  let walkT = 0, checkpoint = { x: body.x, y: body.y }, finished = false, t = 0;
  const t0 = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Level ', h('strong', null, '1'), ' of 3'), h('span.muted', null, 'Gems: ', h('strong', null, '0')));
  const stage = h('div');
  host.append(head, stage);
  const S = makeSurface(stage, { height: ROWS * T, draw: false });
  const pad = makePad(host, { action: 'Jump' });
  const cam: Camera = { x: 0, y: 0, w: S.w, h: ROWS * T };
  ctx.say('Run with the arrows, Jump to hop. Grab every gem and reach the flag.');

  function nextLevel() {
    levelIdx++;
    if (levelIdx >= 3) { finish(); return; }
    ({ map, gems, slimes } = genLevel(ctx.rng, levelIdx));
    totalGems += gems;
    body.x = T * 1.5; body.y = (ROWS - 2) * T - 26; body.vx = 0; body.vy = 0;
    checkpoint = { x: body.x, y: body.y };
    cam.x = 0;
    (head.firstElementChild!.querySelector('strong') as HTMLElement).textContent = String(levelIdx + 1);
    ctx.say(`Level ${levelIdx + 1}!`);
  }

  function finish() {
    if (finished) return;
    finished = true;
    ctx.sound('chime');
    if (got === totalGems) ctx.charm('charm.gemrun');
    setTimeout(() => ctx.end({ attempts: 3, correct: 3, perfect: got === totalGems && falls === 0, durationMs: performance.now() - t0, notes: [`Gems: ${got} of ${totalGems}`, falls ? `Tumbles: ${falls}` : 'Not a single tumble'] }), 700);
  }

  function update(dt: number) {
    if (finished) return;
    t += dt;
    const want = (pad.right ? 1 : 0) - (pad.left ? 1 : 0);
    body.vx = want * RUN;
    if (want) facing = want > 0 ? 'right' : 'left';
    if (pad.action && body.onGround) { body.vy = JUMP; ctx.sound('tick'); }
    body.vy += GRAVITY * dt; if (body.vy > 620) body.vy = 620;
    moveBody(map, body, dt);
    walkT = want ? walkT + dt * 8 : 0;
    const cx = Math.floor((body.x + body.w / 2) / T), cy = Math.floor((body.y + body.h / 2) / T);
    const under = Math.floor((body.y + body.h + 1) / T);
    const id = map.data[cy * map.cols + cx];
    if (id === GEM) { setTile(map, cx, cy, SKY); got++; total++; ctx.sound('pop'); (head.lastElementChild!.querySelector('strong') as HTMLElement).textContent = String(got); }
    if (id === CHECK) { checkpoint = { x: cx * T + 2, y: body.y }; }
    if (id === SPRING || map.data[under * map.cols + cx] === SPRING) { if (body.vy >= 0 && body.onGround) { body.vy = SPRING_V; ctx.sound('ding'); } }
    if (id === FLAG) { nextLevel(); return; }
    // slimes bounce her back to the checkpoint
    for (const s of slimes) {
      s.x += s.dir * 40 * dt; if (s.x < s.min || s.x > s.max) s.dir *= -1;
      if (Math.abs(body.x + body.w / 2 - (s.x + 10)) < 14 && Math.abs(body.y + body.h - (s.y + 20)) < 20) {
        if (body.vy > 0 && body.y + body.h < s.y + 12) { body.vy = JUMP * 0.7; s.x = -9999; s.min = -9999; s.max = -9999; ctx.sound('pop'); }
        else respawn('A slime! Back to your last flag.');
      }
    }
    if (body.y > ROWS * T + 20) respawn('Whoops! Back to your last flag.');
    followCamera(cam, map, body.x + body.w / 2, ROWS * T / 2, 0.2);
  }
  function respawn(msg: string) { falls++; body.x = checkpoint.x; body.y = checkpoint.y - 2; body.vx = 0; body.vy = 0; ctx.sound('buzz'); ctx.say(msg); }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    cam.w = W;
    const skyTop = ['#bfe3f5', '#f6d9b8', '#2d3a6b'][levelIdx];
    c.fillStyle = skyTop; c.fillRect(0, 0, W, H);
    c.fillStyle = levelIdx === 2 ? '#3b4a7a' : '#8fd08a';
    for (let i = -1; i < 6; i++) { const hx = i * 170 - (cam.x * 0.3) % 170; c.beginPath(); c.ellipse(hx, H * 0.8, 120, 44, 0, 0, 7); c.fill(); }
    drawMap(c, map, cam, (id, sx, sy, size) => {
      if (id === GROUND) { c.fillStyle = '#9ad67e'; c.fillRect(sx, sy, size, size); c.fillStyle = '#8a5a3b'; c.fillRect(sx, sy + 7, size, size - 7); }
      else if (id === BRICK) { c.fillStyle = '#8a5a3b'; c.fillRect(sx, sy, size, size); c.fillStyle = '#a0622d'; c.fillRect(sx + 2, sy + 2, size / 2 - 3, size / 2 - 3); c.fillRect(sx + size / 2 + 1, sy + size / 2 + 1, size / 2 - 3, size / 2 - 3); }
      else if (id === GEM) { const bob = Math.sin(t * 4 + sx) * 2; c.fillStyle = '#4f8fe6'; c.beginPath(); c.moveTo(sx + size / 2, sy + 4 + bob); c.lineTo(sx + size - 5, sy + size / 2 + bob); c.lineTo(sx + size / 2, sy + size - 4 + bob); c.lineTo(sx + 5, sy + size / 2 + bob); c.closePath(); c.fill(); }
      else if (id === FLAG) { c.fillStyle = '#9a9aa3'; c.fillRect(sx + size / 2 - 1, sy - size, 3, size * 2); c.fillStyle = '#5fbf6a'; c.beginPath(); c.moveTo(sx + size / 2 + 2, sy - size); c.lineTo(sx + size + 8, sy - size + 8); c.lineTo(sx + size / 2 + 2, sy - size + 16); c.fill(); }
      else if (id === SPRING) { c.fillStyle = '#e2574c'; c.fillRect(sx + 4, sy + size - 8, size - 8, 8); c.strokeStyle = '#9a9aa3'; c.lineWidth = 2; c.beginPath(); for (let k = 0; k < 4; k++) { c.moveTo(sx + 6, sy + size - 8 - k * 4); c.lineTo(sx + size - 6, sy + size - 10 - k * 4); } c.stroke(); }
      else if (id === CHECK) { c.fillStyle = '#9a9aa3'; c.fillRect(sx + size / 2 - 1, sy, 2, size); c.fillStyle = '#f5c542'; c.fillRect(sx + size / 2 + 1, sy + 2, 9, 7); }
    });
    for (const s of slimes) { if (s.x < -1000) continue; const sx = s.x - cam.x, sy = s.y - cam.y; c.fillStyle = '#5fbf6a'; c.beginPath(); c.ellipse(sx + 10, sy + 16, 10, 7 + Math.sin(t * 6) * 1.5, 0, 0, 7); c.fill(); c.fillStyle = '#2a2a2e'; c.fillRect(sx + 6, sy + 12, 2, 2); c.fillRect(sx + 12, sy + 12, 2, 2); }
    const frame = walkT > 0 ? Math.floor(walkT) % 4 : 0;
    drawSprite(c, ctx.avatar, body.x + body.w / 2 - cam.x, body.y + body.h - cam.y, body.h / 32, frame, facing);
  }

  const loop = makeLoop(update, draw);
  loop.start();
  void total;
  return { destroy() { loop.stop(); pad.destroy(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'fun.gem-run',
  title: 'Gem Run',
  blurb: 'Run, jump, bounce on springs, collect every gem, reach the flag. Three levels.',
  category: 'fun',
  skills: [],
  levelRange: [1, 10],
  unlock: { gems: 200 },
  mount,
};
export default mod;
